import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, FolderKanban, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import TemplatePickerDialog from "@/components/TemplatePickerDialog";
import FileDropZone from "@/components/FileDropZone";

const TYPES = ["film","video","campaign","client job","event","workshop","internal project","consulting project","training project"];
const STATUS = ["idea","active","waiting","completed","archived"];
const STATUS_COLOR = { idea: "secondary", active: "default", waiting: "secondary", completed: "outline", archived: "outline" };

export default function Projects() {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const load = async () => {
    const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
    const { data } = await api.get("/projects", { params }); setList(data);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const save = async (form) => {
    try {
      const clean = {
        ...form,
        budget: form.budget === "" || form.budget == null ? null : form.budget,
        deadline: form.deadline || null,
      };
      if (editing?.id) await api.put(`/projects/${editing.id}`, clean);
      else await api.post("/projects", clean);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete project?")) return; await api.delete(`/projects/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">Operational work — films, jobs, campaigns and consulting.</p>
        </div>
        <div className="md:ml-auto flex gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="project-status-filter" className="w-40"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-project-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> Blank project</Button></DialogTrigger>
            <ProjectDialog project={editing} onSave={save}/>
          </Dialog>
          <Button data-testid="new-project-from-template-btn" onClick={() => setTemplatePickerOpen(true)}>
            <Sparkles className="w-4 h-4 mr-2" /> From template
          </Button>
        </div>
      </div>

      <TemplatePickerDialog
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onCreated={() => load()}
      />

      {list.length === 0 ? (
        <Card className="studio-card p-10 text-center">
          <FolderKanban className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5}/>
          <div className="text-lg font-medium">No projects yet</div>
          <p className="text-sm text-muted-foreground mt-1">Start a project to link people, tasks, invoices and documents.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((p) => (
            <Card key={p.id} className="studio-card p-5 group hover:border-[hsl(var(--studio-primary)/0.35)] transition-colors" data-testid={`project-card-${p.id}`}>
              <Link to={`/projects/${p.id}`} className="block" data-testid={`project-open-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-medium truncate group-hover:text-[hsl(var(--studio-primary))]">{p.title}</div>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] font-normal">{p.type}</Badge>
                      <Badge variant={STATUS_COLOR[p.status] || "secondary"} className="text-[10px] font-normal capitalize">{p.status}</Badge>
                    </div>
                  </div>
                </div>
                {p.description && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{p.description}</p>}
                {(p.stages || []).length > 0 && (
                  <div className="mt-3" data-testid={`project-stages-${p.id}`}>
                    <div className="flex items-center gap-1 flex-wrap">
                      {p.stages.slice().sort((a, b) => a.order - b.order).map((s) => (
                        <span
                          key={s.key}
                          className={cn(
                            "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded",
                            s.status === "done" ? "bg-[hsl(var(--halo-teal)/0.15)] text-[hsl(var(--halo-teal))] line-through" :
                            s.status === "active" ? "bg-[hsl(var(--studio-primary)/0.12)] text-[hsl(var(--studio-primary))] font-medium" :
                            "bg-accent text-muted-foreground"
                          )}
                          title={s.description || s.name}
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 text-xs text-muted-foreground flex justify-between">
                  <span>{p.deadline ? `Deadline ${p.deadline}` : "No deadline"}</span>
                  <span>{p.budget ? money(p.budget) : ""}</span>
                </div>
              </Link>
              <div className="mt-3 pt-3 border-t border-border flex justify-end gap-1">
                <Button variant="ghost" size="sm" data-testid={`edit-project-${p.id}`} onClick={(e) => { e.stopPropagation(); setEditing(p); setOpen(true); }}><Edit3 className="w-3.5 h-3.5"/></Button>
                <Button variant="ghost" size="sm" data-testid={`delete-project-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p.id); }}><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectDialog({ project, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      title: project?.title || "",
      type: project?.type || "client job",
      status: project?.status || "active",
      description: project?.description || "",
      deadline: project?.deadline || "",
      budget: project?.budget || "",
      notes: project?.notes || "",
    });
  }, [project]);
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>{project?.id ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <Field label="Title"><Input data-testid="project-title" value={f.title || ""} onChange={(e) => setF({...f, title: e.target.value})}/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={f.type} onValueChange={(v) => setF({...f, type: v})}>
              <SelectTrigger data-testid="project-type"><SelectValue/></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={f.status} onValueChange={(v) => setF({...f, status: v})}>
              <SelectTrigger data-testid="project-status"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Description"><Textarea rows={3} value={f.description || ""} onChange={(e) => setF({...f, description: e.target.value})}/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Deadline"><Input type="date" value={f.deadline || ""} onChange={(e) => setF({...f, deadline: e.target.value})}/></Field>
          <Field label="Budget"><Input type="number" step="0.01" value={f.budget || ""} onChange={(e) => setF({...f, budget: e.target.value ? parseFloat(e.target.value) : null})}/></Field>
        </div>
        {project?.id && (
          <div className="pt-3 border-t border-border space-y-1.5">
            <Label className="text-xs">Attachments</Label>
            <FileDropZone
              context={{ project_id: project.id }}
              compact
              data-testid="project-dropzone"
              label="Drop files to attach to this project"
            />
          </div>
        )}
      </div>
      <DialogFooter><Button data-testid="project-save" onClick={() => onSave(f)}>Save</Button></DialogFooter>
    </DialogContent>
  );
}
const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
